const getScrollLock = () => {
  return (
    window.sharedScrollLock || {
      disablePageScroll: () => {},
      enablePageScroll: () => {},
      pageScrollIsDisabled: () => false,
      markScrollable: () => {}
    }
  );
};
 
/**
 * DialogComponent class for managing dialog functionality.
 * This class extends HTMLElement and provides a dialog component
 * with focus trapping, scroll locking, and click handling.
 */

class DialogComponent extends HTMLElement {
  constructor() {
    super();
    this.dialog = null;
    this.isOpen = false;
    this._boundClick = this._handleDocumentClick.bind(this);
    this._boundKeydown = this._handleKeydown.bind(this);
  }

  connectedCallback() {
    if (typeof window.A11yDialog !== 'function') {
      console.warn('A11yDialog is not available on window.');
      return;
    }

    const dialogId = this.getAttribute('id');
    if (!dialogId) return;

    this.dialogContent = this.querySelector('[data-a11y-dialog-content]');

    if(!this.hasAttribute("data-a11y-stay-in-place")){
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }
    }

    document.addEventListener('click', this._boundClick, true);
    document.addEventListener('keydown', this._boundKeydown, true);

    this.dialog = new window.A11yDialog(this);
    if (typeof window.patchA11yDialogScroll === 'function') {
      window.patchA11yDialogScroll(this.dialog, this);
    }

    this.markScrollableElements();

    this.dialog.on('show', () => {
      this.isOpen = true;
      this.classList.add('active');
      if (!this.hasAttribute('allow-scroll')) {
        getScrollLock().disablePageScroll();
      }
    });

    this.dialog.on('hide', () => {
      this.isOpen = false;
      this.classList.remove('active');
      if (!this.hasAttribute('allow-scroll')) {
        getScrollLock().enablePageScroll();
      }
    });

    document.querySelectorAll(`[data-a11y-dialog-toggle="${dialogId}"]`).forEach(toggle => {  
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (this.isOpen) {
          this.close();
        } else {
          this.open();
        }
      });
    });

    // Hash open
    const checkHash = () => {
      if (window.location.hash.slice(1) === dialogId) this.open();
    };
    checkHash();
    window.addEventListener('hashchange', checkHash);
    this._hashHandler = checkHash;
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._boundClick, true);
    document.removeEventListener('keydown', this._boundKeydown, true);
    if (this._hashHandler) window.removeEventListener('hashchange', this._hashHandler);
  }

  markScrollableElements() {
    this.querySelectorAll('[data-scroll-lock-scrollable]').forEach(el => {
      getScrollLock().markScrollable(el);
    });
  }


  _handleDocumentClick(e) {
    const dialogId = this.getAttribute('id');
    if (!dialogId) return;

    // Use composedPath() for Shadow DOM support (matches a11y-dialog behavior)
    const clickTarget = (e.composedPath && e.composedPath()[0]) || e.target;
    const trigger = clickTarget.closest?.(
      `[data-a11y-dialog-show="${dialogId}"], [data-a11y-dialog-toggle="${dialogId}"]`
    );
    if (trigger) {
      e.preventDefault();
      e.stopImmediatePropagation();
      this.toggle(trigger);
      return;
    }

    if (
      !this.isOpen ||
      !this.hasAttribute('close-on-outside-click') ||
      !this.dialogContent
    ) {
      return;
    }

    if (this.dialogContent.contains(clickTarget)) {
      return;
    }

    this.close();
  }

  _handleKeydown(e) {
    if (!this.isOpen) return;
    const key = e.key || e.code;
    if (key === 'Escape' || key === 'Esc' || e.keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      this.close();
    }
  }

  toggle(trigger) {
    if (this.isOpen) {
      this.close(trigger);
      this.classList.remove('active');
      trigger.classList.remove('active');
    } else {
      this.open(trigger);
      this.classList.add('active');
      trigger.classList.add('active');
    }
  }

  open(trigger) {
    if (!this.dialog) return;
    this._openingTrigger = trigger;
    this.dialog.show(trigger ? { target: trigger } : undefined);
  }

  close(trigger) {
    if (!this.dialog) return;
    this.dialog.hide(trigger ? { target: trigger } : undefined);
  }

  show() {
    this.open();
  }

  hide() {
    this.close();
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define('dialog-component', DialogComponent);
}